import { isEmpty, isNil, isNumber, some } from 'lodash';

const CONTRACTOR_TAX_CLASSIFICATION = '1099';
const EMPLOYEE_TAX_CLASSIFICATION = 'w2';

/**
 * Checks if the job tax classification is for a contractor
 */
export const isAContractor = (jobTaxClassification: string | null): boolean =>
  jobTaxClassification === CONTRACTOR_TAX_CLASSIFICATION;

/**
 * Checks if a field is blank (null, undefined, or empty string)
 */
export const isBlank = (field: any): boolean =>
  isNil(field) || (!isNumber(field) && isEmpty(field));

/**
 * Checks if an employee is eligible for payroll
 */
export const eligibleEmployeeForPayroll = ({
  includedInPayroll,
  jobTaxClassification,
  hireDate,
  wageRate,
}: {
  includedInPayroll: boolean;
  jobTaxClassification: string | null;
  hireDate: any;
  wageRate: any;
}): boolean =>
  includedInPayroll &&
  jobTaxClassification === EMPLOYEE_TAX_CLASSIFICATION &&
  !isEmpty(hireDate) &&
  !isBlank(wageRate);

/**
 * Checks if a contractor is eligible for payroll
 */
export const eligibleContractorForPayroll = ({
  includedInPayroll,
  jobTaxClassification,
  wageRate,
  contractorType,
}: {
  includedInPayroll: boolean;
  jobTaxClassification: string | null;
  wageRate: any;
  contractorType: any;
}): boolean =>
  includedInPayroll &&
  jobTaxClassification === CONTRACTOR_TAX_CLASSIFICATION &&
  !isBlank(wageRate) &&
  !isBlank(contractorType);

/**
 * Checks if the first name field is not filled
 */
export const notFilled = (firstName: string): boolean => !firstName;

/**
 * Checks if there are any validation errors
 */
export const hasErrors = (errors: any): boolean =>
  some(errors, fields => fields && some(fields, error => !!error));

/**
 * Checks if the tax classification can be edited
 */
export const canEditTaxClassification = ({
  editingUser,
  hasNoActivePayments,
  hasITIN,
}: {
  editingUser: any;
  hasNoActivePayments: boolean;
  hasITIN: boolean;
}): boolean => {
  if (editingUser) {
    if (editingUser.jobTaxClassification === '1099') {
      return hasNoActivePayments && !hasITIN;
    }

    return hasNoActivePayments;
  }

  return true;
};

/**
 * Checks if the hiring banner should be shown
 */
export const showHiringBanner = ({
  addMore,
  isHiringBannerEnabled,
  hiringBannerDismissed,
}: {
  addMore: boolean;
  isHiringBannerEnabled: boolean;
  hiringBannerDismissed: boolean;
}): boolean =>
  !window.location.pathname.includes('/schedule') &&
  !addMore &&
  isHiringBannerEnabled &&
  !hiringBannerDismissed;

/**
 * Checks if the success view should timeout
 */
export const shouldSuccessViewTimeout = ({
  isHiringBannerEnabled,
  hiringBannerDismissed,
  addMore,
}: {
  isHiringBannerEnabled: boolean;
  hiringBannerDismissed: boolean;
  addMore: boolean;
}): boolean => !isHiringBannerEnabled || hiringBannerDismissed || addMore;

/**
 * Checks if the submit button should be disabled
 */
export const isSubmitButtonDisabled = ({
  firstName,
  errors,
  isPayrollEnrolled,
  canRequestInformation,
  isEligibleForPayroll,
  invalidPayrollInfo,
  user,
  requestInformation,
}: {
  firstName: string;
  errors: any;
  isPayrollEnrolled: boolean;
  canRequestInformation: boolean;
  isEligibleForPayroll: boolean | null;
  invalidPayrollInfo: (user: any) => boolean;
  user: any;
  requestInformation: string | null;
}): boolean =>
  notFilled(firstName) ||
  hasErrors(errors) ||
  (isPayrollEnrolled &&
    (!canRequestInformation ||
      isEligibleForPayroll === false ||
      (invalidPayrollInfo(user) && !requestInformation)));

/**
 * Transforms state data into job attributes format
 */
export const jobAttributes = ({
  wageAttributes,
  jobTaxClassification,
  contractorType,
  fourTensRuleEnabled,
  packet,
  hireDate,
  includedInPayroll,
  hiredFrom,
  level,
  currentLocationId,
}: {
  wageAttributes: any[];
  jobTaxClassification: string | null;
  contractorType: string | null;
  fourTensRuleEnabled: boolean;
  packet: string;
  hireDate: any;
  includedInPayroll: boolean;
  hiredFrom: string;
  level: string;
  currentLocationId: number;
}): any[] => {
  const defaultRole = wageAttributes[0] || {};

  return [
    {
      level,
      location_id: currentLocationId,
      default_role_name: defaultRole.role_name,
      wage_rate: defaultRole.wage_rate,
      wage_type: defaultRole.wage_type,
      // `slice(1)` removes the first element from `role_wages_attributes` and return the rest
      role_wages_attributes: wageAttributes.slice(1),
      tax_classification: jobTaxClassification,
      contractor_type: contractorType,
      four_tens_rule_enabled: !!fourTensRuleEnabled,
      onboarding_packet_type: packet,
      hire_date: hireDate,
      included_in_payroll: includedInPayroll,
      is_tipped: defaultRole.is_tipped,
      hired_from: hiredFrom,
    },
  ];
};

/**
 * Transforms state data into user and job data format for API calls
 */
export const userAndJobData = ({
  firstName,
  lastName,
  phone,
  email,
  requestInformation,
  sendSms,
  jobAttributesData,
}: {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  requestInformation: string | null;
  sendSms: boolean;
  jobAttributesData: any[];
}): any => ({
  user: {
    first_name: firstName,
    last_name: lastName,
    phone,
    email,
    all_jobs_attributes: jobAttributesData,
  },
  request_information: requestInformation,
  send_sms: sendSms,
});

/**
 * Checks if payroll information is invalid for a user
 */
export const invalidPayrollInfo = (user: any): boolean => {
  if (user) {
    const isEmployee = user.get('tax_classification') === 'w2';
    const isContractor = user.get('tax_classification') === '1099';
    const individualContractor = user.get('contractor_type') === 'individual';

    if (
      !user.get('legal_first_name') ||
      !user.get('legal_last_name') ||
      (user.get('tax_classification') === 'w2' && !user.get('date_of_birth')) ||
      !user.get('address_1') ||
      !user.get('state') ||
      !user.get('city') ||
      !user.get('zip_code')
    ) {
      return true;
    }

    if (
      (isEmployee && !user.get('decrypted_ssn')) ||
      (isContractor && individualContractor && !user.get('decrypted_ssn'))
    ) {
      return true;
    }

    if (
      isContractor &&
      !individualContractor &&
      (!user.get('contractor_info_decrypted_ein') ||
        !user.get('contractor_info_business_name'))
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Determines the payroll eligibility status for a user
 */
export const getUserPayrollEligibility = ({
  includedInPayroll,
  jobTaxClassification,
  hireDate,
  wageRate,
  contractorType,
}: {
  includedInPayroll: boolean;
  jobTaxClassification: string | null;
  hireDate: any;
  wageRate: any;
  contractorType: string | null;
}): boolean | null => {
  if (includedInPayroll) {
    if (
      eligibleEmployeeForPayroll({
        includedInPayroll,
        jobTaxClassification,
        hireDate,
        wageRate,
      }) ||
      eligibleContractorForPayroll({
        includedInPayroll,
        jobTaxClassification,
        wageRate,
        contractorType,
      })
    ) {
      return true;
    }
    return false;
  }
  return null;
};

/**
 * Determines if request information can be processed
 */
export const canRequestInformation = ({
  phone,
  email,
  jobTaxClassification,
  contractorType,
  requestInformation,
}: {
  phone: string;
  email: string;
  jobTaxClassification: string | null;
  contractorType: string | null;
  requestInformation: string | null;
}): boolean => {
  if (
    ((isEmpty(phone) && isEmpty(email)) ||
      isEmpty(jobTaxClassification) ||
      (isAContractor(jobTaxClassification) && isEmpty(contractorType))) &&
    requestInformation === 'packet'
  ) {
    return false;
  }
  return true;
};

/**
 * Organizes state into logical groups for easier functional component conversion
 */
export const organizeState = (state: any) => ({
  // User information
  userInfo: {
    id: state.id,
    firstName: state.firstName,
    lastName: state.lastName,
    fullName: state.fullName,
    phone: state.phone,
    email: state.email,
    avatarUrl: state.avatarUrl,
  },

  // Job information
  jobInfo: {
    packet: state.packet,
    jobTaxClassification: state.jobTaxClassification,
    contractorType: state.contractorType,
    fourTensRuleEnabled: state.fourTensRuleEnabled,
    level: state.level,
    hireDate: state.hireDate,
    wageAttributes: state.wageAttributes,
    hiredFrom: state.hiredFrom,
  },

  // Payroll information
  payrollInfo: {
    hasITIN: state.hasITIN,
    hasNoActivePayments: state.hasNoActivePayments,
    hasPaidPayrollRunsForJob: state.hasPaidPayrollRunsForJob,
    hasJobsAtMoreThanOneCompany: state.hasJobsAtMoreThanOneCompany,
    includedInPayroll: state.includedInPayroll,
    requestInformation: state.requestInformation,
    canRequestInformation: state.canRequestInformation,
    isEligibleForPayroll: state.isEligibleForPayroll,
  },

  // UI state
  uiState: {
    view: state.view,
    addMore: state.addMore,
    sendSms: state.sendSms,
    submitting: state.submitting,
    formKey: state.formKey,
    errors: state.errors,
  },
});

/**
 * Initial state for user information
 */
export const getInitialUserInfo = () => ({
  id: null,
  firstName: '',
  lastName: '',
  fullName: '',
  phone: '',
  email: '',
  avatarUrl: '',
});

/**
 * Initial state for job information
 */
export const getInitialJobInfo = () => ({
  packet: 'w2',
  jobTaxClassification: null,
  contractorType: null,
  fourTensRuleEnabled: false,
  level: 'Employee',
  hireDate: null,
  wageAttributes: [],
  hiredFrom: '',
});

/**
 * Initial state for payroll information
 */
export const getInitialPayrollInfo = () => ({
  hasITIN: false,
  hasNoActivePayments: false,
  hasPaidPayrollRunsForJob: false,
  hasJobsAtMoreThanOneCompany: false,
  includedInPayroll: true,
  requestInformation: null,
  canRequestInformation: null,
  isEligibleForPayroll: null,
});

/**
 * Initial state for UI state
 */
export const getInitialUIState = () => ({
  view: 'Form',
  addMore: false,
  sendSms: true,
  submitting: false,
  formKey: null,
  errors: {},
});
